import { useMemo, useState } from "react";
import {
  BarChart2,
  Plus,
  Trash2,
  Pencil,
  Search,
  TrendingUp,
  TrendingDown,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { PlayerSelect, type PlayerOption } from "@/components/PlayerSelect";
import { ClubSelect } from "@/components/ClubSelect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { API_ENDPOINTS } from "@/api/endpoints";
import { toast } from "react-toastify";
import { cn } from "@/lib/utils";

// ─── Types ────────────────────────────────────────────────────────────────────

type StatType = "GOALS" | "ASSISTS";

interface PlayerRef {
  id: number;
  name: string;
  photo: string | null;
  position: string;
  // Career totals come from the Player record now — falls back to
  // statValue on the card when null.
  careerGoals: number | null;
  careerAssists: number | null;
  nation: { code: string } | null;
  club: { name: string } | null;
}

interface StatCard {
  id: number;
  playerId: number;
  statLabel: string;
  statValue: number;
  type: StatType;
  order: number;
  isActive: boolean;
  player: PlayerRef;
}

interface TransferCard {
  id: number;
  playerId: number;
  toClubId: number;
  fee: number;
  year: number;
  order: number;
  isActive: boolean;
  player: PlayerRef;
  toClub: { id: number; name: string; logoUrl: string | null };
}

const PAGE_SIZE = 15;

const STAT_CONFIG: Record<
  StatType,
  { label: string; defaultStatLabel: string; placeholder: string }
> = {
  GOALS: {
    label: "Goals",
    defaultStatLabel: "Career Goals",
    placeholder: "e.g. 821",
  },
  ASSISTS: {
    label: "Assists",
    defaultStatLabel: "Career Assists",
    placeholder: "e.g. 365",
  },
};

// ─── Stat tab (Goals + Assists share this) ───────────────────────────────────

function StatTab({ mode }: { mode: StatType }) {
  const qc = useQueryClient();
  const cfg = STAT_CONFIG[mode];

  const { data, isLoading } = useQuery<StatCard[]>({
    queryKey: ["quiz", "hol", "stat", mode],
    queryFn: async () => {
      const r = (await apiClient.get(
        `${API_ENDPOINTS.QUIZ.HOL_LIST}?mode=${mode}`,
      )) as unknown as StatCard[] | null;
      return r ?? [];
    },
  });
  const cards = data ?? [];

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["quiz", "hol", "stat", mode] });

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: (d: object) => apiClient.post(API_ENDPOINTS.QUIZ.HOL_CREATE, d),
    onSuccess: () => {
      toast.success("Card created");
      invalidate();
      closeDialog();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      toast.error(e.response?.data?.message || "Failed to create"),
  });

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, d }: { id: number; d: object }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.HOL_UPDATE(id), d),
    onSuccess: () => {
      toast.success("Updated");
      invalidate();
      closeDialog();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      toast.error(e.response?.data?.message || "Failed to update"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) => apiClient.delete(API_ENDPOINTS.QUIZ.HOL_DELETE(id)),
    onSuccess: () => {
      toast.success("Deleted");
      invalidate();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      toast.error(e.response?.data?.message || "Failed to delete"),
  });

  const { mutate: toggle } = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.HOL_UPDATE(id), { isActive }),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Toggle failed"),
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StatCard | null>(null);
  const emptyForm = {
    playerId: 0,
    playerName: "",
    playerImageUrl: "",
    statValue: "" as string | number,
    order: "" as string | number,
  };
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<{
    playerId?: string;
    statValue?: string;
  }>({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: cards.length });
    setOpen(true);
  };

  const openEdit = (c: StatCard) => {
    setEditing(c);
    setForm({
      playerId: c.player.id,
      playerName: c.player.name,
      playerImageUrl: c.player.photo ?? "",
      statValue: c.statValue,
      order: c.order,
    });
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditing(null);
    setErrors({});
  };

  const handlePlayerChange = (p: PlayerOption | null) => {
    setForm((f) => ({
      ...f,
      playerId: p?.id ?? 0,
      playerName: p?.name ?? "",
      playerImageUrl: p?.imageUrl ?? "",
    }));
    if (p) setErrors((er) => ({ ...er, playerId: undefined }));
  };

  const handleSave = () => {
    const newErrors: typeof errors = {};
    if (!form.playerId) newErrors.playerId = "Please select a player";
    if (form.statValue === "" || isNaN(Number(form.statValue)))
      newErrors.statValue = "A valid stat value is required";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    if (!editing) {
      const dup = cards.find((c) => c.player.id === form.playerId);
      if (dup) {
        setErrors({
          playerId: `${form.playerName} already has a ${cfg.label.toLowerCase()} card`,
        });
        return;
      }
    }
    setErrors({});

    const payload = {
      playerId: form.playerId,
      statLabel: cfg.defaultStatLabel,
      statValue: Number(form.statValue),
      type: mode,
      order: form.order !== "" ? Number(form.order) : cards.length,
    };

    if (editing) update({ id: editing.id, d: payload });
    else create(payload);
  };

  const filtered = [...cards]
    .sort((a, b) => a.order - b.order)
    .filter((c) => {
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      return (
        c.player.name.toLowerCase().includes(s) ||
        c.player.club?.name?.toLowerCase().includes(s) ||
        c.player.nation?.code?.toLowerCase().includes(s)
      );
    });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  // Surfaced value: prefer Player.career*, fall back to card.statValue
  const surfaceValue = (c: StatCard) => {
    const v = mode === "ASSISTS" ? c.player.careerAssists : c.player.careerGoals;
    return v ?? c.statValue;
  };

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">
          {cards.length} {cfg.label.toLowerCase()} cards ·{" "}
          {cards.filter((c) => c.isActive).length} active
        </p>
        <Button
          onClick={openCreate}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          size="sm"
        >
          <Plus className="h-4 w-4 mr-1" /> Add {cfg.label} Card
        </Button>
      </div>

      <div className="relative max-w-xs mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search by player, club, nation…"
          className="pl-8 h-9 text-sm"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <BarChart2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{search ? "No matches" : "No cards yet"}</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5 font-medium w-8">#</th>
                  <th className="text-left px-4 py-2.5 font-medium">Player</th>
                  <th className="text-left px-4 py-2.5 font-medium">Nat</th>
                  <th className="text-left px-4 py-2.5 font-medium">Club</th>
                  <th className="text-right px-4 py-2.5 font-medium">
                    {cfg.label}
                  </th>
                  <th className="text-left px-4 py-2.5 font-medium">Active</th>
                  <th className="px-4 py-2.5 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paged.map((card) => (
                  <tr
                    key={card.id}
                    className={cn(
                      "hover:bg-gray-50 transition-colors",
                      !card.isActive && "opacity-50",
                    )}
                  >
                    <td className="px-4 py-2.5 text-gray-400 text-xs">
                      {card.order}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {card.player.photo ? (
                          <img
                            src={card.player.photo}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                            <span className="text-orange-600 text-xs font-bold">
                              {card.player.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 leading-none">
                            {card.player.name}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {card.player.position}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 text-xs">
                      {card.player.nation?.code ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 max-w-[120px] truncate text-xs">
                      {card.player.club?.name ?? "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="inline-flex items-center gap-1 font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md text-sm">
                        {surfaceValue(card) > 0 ? (
                          <TrendingUp className="h-3 w-3" />
                        ) : (
                          <TrendingDown className="h-3 w-3" />
                        )}
                        {surfaceValue(card)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <Switch
                        checked={card.isActive}
                        onCheckedChange={(v) =>
                          toggle({ id: card.id, isActive: v })
                        }
                        className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-400"
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => openEdit(card)}
                        >
                          <Pencil className="h-3.5 w-3.5 text-gray-400" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => {
                            if (
                              confirm(`Delete card for ${card.player.name}?`)
                            )
                              remove(card.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination
              page={safePage}
              totalPages={totalPages}
              total={filtered.length}
              setPage={setPage}
            />
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? `Edit: ${editing.player.name}`
                : `New ${cfg.label} Card`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block text-sm">
                Player <span className="text-red-500">*</span>
              </Label>
              <PlayerSelect
                value={
                  form.playerName
                    ? {
                        name: form.playerName,
                        imageUrl: form.playerImageUrl,
                      }
                    : null
                }
                onChange={handlePlayerChange}
              />
              {errors.playerId && (
                <p className="text-xs text-red-500 mt-1 font-medium">
                  {errors.playerId}
                </p>
              )}
            </div>
            <div>
              <Label className="mb-1 block text-sm">
                {cfg.label} (fallback) <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                value={form.statValue}
                onChange={(e) => {
                  setForm((f) => ({ ...f, statValue: e.target.value }));
                  if (e.target.value !== "")
                    setErrors((er) => ({ ...er, statValue: undefined }));
                }}
                placeholder={cfg.placeholder}
                min={0}
                className={errors.statValue ? "border-red-400" : ""}
              />
              {errors.statValue && (
                <p className="text-xs text-red-500 mt-1 font-medium">
                  {errors.statValue}
                </p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Used only when the player record has no career
                {mode === "ASSISTS" ? " assists" : " goals"} set. Set it
                on the Player record for it to be authoritative.
              </p>
            </div>
            <div>
              <Label className="mb-1 block text-sm">Display Order</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) =>
                  setForm((f) => ({ ...f, order: e.target.value }))
                }
                placeholder="0"
                min={0}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {isCreating || isUpdating
                ? "Saving…"
                : editing
                  ? "Update"
                  : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Transfers tab ───────────────────────────────────────────────────────────

function TransfersTab() {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<TransferCard[]>({
    queryKey: ["quiz", "hol", "transfers"],
    queryFn: async () => {
      const r = (await apiClient.get(
        API_ENDPOINTS.QUIZ.HOL_TRANSFER_LIST,
      )) as unknown as TransferCard[] | null;
      return r ?? [];
    },
  });
  const transfers = data ?? [];

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["quiz", "hol", "transfers"] });

  const { mutate: create, isPending: isCreating } = useMutation({
    mutationFn: (d: object) =>
      apiClient.post(API_ENDPOINTS.QUIZ.HOL_TRANSFER_CREATE, d),
    onSuccess: () => {
      toast.success("Transfer created");
      invalidate();
      closeDialog();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      toast.error(e.response?.data?.message || "Failed to create"),
  });

  const { mutate: update, isPending: isUpdating } = useMutation({
    mutationFn: ({ id, d }: { id: number; d: object }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.HOL_TRANSFER_UPDATE(id), d),
    onSuccess: () => {
      toast.success("Updated");
      invalidate();
      closeDialog();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      toast.error(e.response?.data?.message || "Failed to update"),
  });

  const { mutate: remove } = useMutation({
    mutationFn: (id: number) =>
      apiClient.delete(API_ENDPOINTS.QUIZ.HOL_TRANSFER_DELETE(id)),
    onSuccess: () => {
      toast.success("Deleted");
      invalidate();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) =>
      toast.error(e.response?.data?.message || "Failed to delete"),
  });

  const { mutate: toggle } = useMutation({
    mutationFn: ({ id, isActive }: { id: number; isActive: boolean }) =>
      apiClient.patch(API_ENDPOINTS.QUIZ.HOL_TRANSFER_UPDATE(id), { isActive }),
    onSuccess: () => invalidate(),
    onError: () => toast.error("Toggle failed"),
  });

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<TransferCard | null>(null);
  const emptyForm = {
    playerId: 0,
    playerName: "",
    playerImageUrl: "",
    toClub: null as { id: number; name: string; logoUrl: string | null } | null,
    fee: "" as string | number,
    year: "" as string | number,
    order: "" as string | number,
  };
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState<{
    playerId?: string;
    toClub?: string;
    fee?: string;
    year?: string;
  }>({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...emptyForm, order: transfers.length });
    setOpen(true);
  };

  const openEdit = (t: TransferCard) => {
    setEditing(t);
    setForm({
      playerId: t.player.id,
      playerName: t.player.name,
      playerImageUrl: t.player.photo ?? "",
      toClub: {
        id: t.toClub.id,
        name: t.toClub.name,
        logoUrl: t.toClub.logoUrl,
      },
      fee: t.fee,
      year: t.year,
      order: t.order,
    });
    setOpen(true);
  };

  const closeDialog = () => {
    setOpen(false);
    setEditing(null);
    setErrors({});
  };

  const handleSave = () => {
    const newErrors: typeof errors = {};
    if (!form.playerId) newErrors.playerId = "Please select a player";
    if (!form.toClub) newErrors.toClub = "Please select a destination club";
    if (form.fee === "" || isNaN(Number(form.fee)) || Number(form.fee) < 0)
      newErrors.fee = "Enter a valid fee in millions";
    if (form.year === "" || isNaN(Number(form.year)))
      newErrors.year = "Enter a valid year";
    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      return;
    }
    setErrors({});

    const payload = {
      playerId: form.playerId,
      toClubId: form.toClub!.id,
      fee: Number(form.fee),
      year: Number(form.year),
      order: form.order !== "" ? Number(form.order) : transfers.length,
    };

    if (editing) update({ id: editing.id, d: payload });
    else create(payload);
  };

  const filtered = useMemo(
    () =>
      [...transfers]
        .sort((a, b) => a.order - b.order)
        .filter((t) => {
          if (!search.trim()) return true;
          const s = search.toLowerCase();
          return (
            t.player.name.toLowerCase().includes(s) ||
            t.toClub.name.toLowerCase().includes(s) ||
            String(t.year).includes(s)
          );
        }),
    [transfers, search],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paged = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-gray-500">
          {transfers.length} transfers ·{" "}
          {transfers.filter((t) => t.isActive).length} active
        </p>
        <Button
          onClick={openCreate}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          size="sm"
        >
          <Plus className="h-4 w-4 mr-1" /> Add Transfer
        </Button>
      </div>

      <div className="relative max-w-xs mb-4">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <Input
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          placeholder="Search player, club, year…"
          className="pl-8 h-9 text-sm"
        />
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 text-gray-400">
          <ArrowRight className="h-12 w-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">
            {search ? "No matches" : "No transfers yet"}
          </p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-gray-50 text-gray-500 text-xs uppercase tracking-wide">
                  <th className="text-left px-4 py-2.5 font-medium w-8">#</th>
                  <th className="text-left px-4 py-2.5 font-medium">Player</th>
                  <th className="text-left px-4 py-2.5 font-medium">To Club</th>
                  <th className="text-right px-4 py-2.5 font-medium">Year</th>
                  <th className="text-right px-4 py-2.5 font-medium">Fee (£m)</th>
                  <th className="text-left px-4 py-2.5 font-medium">Active</th>
                  <th className="px-4 py-2.5 w-20" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {paged.map((t) => (
                  <tr
                    key={t.id}
                    className={cn(
                      "hover:bg-gray-50 transition-colors",
                      !t.isActive && "opacity-50",
                    )}
                  >
                    <td className="px-4 py-2.5 text-gray-400 text-xs">
                      {t.order}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {t.player.photo ? (
                          <img
                            src={t.player.photo}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                            <span className="text-orange-600 text-xs font-bold">
                              {t.player.name.charAt(0)}
                            </span>
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-gray-900 leading-none">
                            {t.player.name}
                          </p>
                          <p className="text-[11px] text-gray-400 mt-0.5">
                            {t.player.position} ·{" "}
                            {t.player.nation?.code ?? "—"}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {t.toClub.logoUrl ? (
                          <img
                            src={t.toClub.logoUrl}
                            alt=""
                            className="w-5 h-5 object-contain"
                          />
                        ) : null}
                        <span className="font-medium text-gray-700">
                          {t.toClub.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-600">
                      {t.year}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <span className="inline-flex items-center gap-1 font-bold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-md text-sm">
                        £{t.fee}m
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <Switch
                        checked={t.isActive}
                        onCheckedChange={(v) =>
                          toggle({ id: t.id, isActive: v })
                        }
                        className="data-[state=checked]:bg-green-500 data-[state=unchecked]:bg-red-400"
                      />
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => openEdit(t)}
                        >
                          <Pencil className="h-3.5 w-3.5 text-gray-400" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => {
                            if (
                              confirm(
                                `Delete ${t.player.name} → ${t.toClub.name}?`,
                              )
                            )
                              remove(t.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-red-400" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <Pagination
              page={safePage}
              totalPages={totalPages}
              total={filtered.length}
              setPage={setPage}
            />
          )}
        </>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>
              {editing
                ? `Edit: ${editing.player.name} → ${editing.toClub.name}`
                : "New Transfer"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label className="mb-1 block text-sm">
                Player <span className="text-red-500">*</span>
              </Label>
              <PlayerSelect
                value={
                  form.playerName
                    ? { name: form.playerName, imageUrl: form.playerImageUrl }
                    : null
                }
                onChange={(p) => {
                  setForm((f) => ({
                    ...f,
                    playerId: p?.id ?? 0,
                    playerName: p?.name ?? "",
                    playerImageUrl: p?.imageUrl ?? "",
                  }));
                  if (p) setErrors((er) => ({ ...er, playerId: undefined }));
                }}
              />
              {errors.playerId && (
                <p className="text-xs text-red-500 mt-1 font-medium">
                  {errors.playerId}
                </p>
              )}
            </div>

            <div>
              <Label className="mb-1 block text-sm">
                To Club <span className="text-red-500">*</span>
              </Label>
              <ClubSelect
                value={
                  form.toClub
                    ? {
                        name: form.toClub.name,
                        logoUrl: form.toClub.logoUrl,
                      }
                    : null
                }
                onChange={(c) => {
                  setForm((f) => ({
                    ...f,
                    toClub: c
                      ? {
                          id: c.id,
                          name: c.name,
                          logoUrl: c.logoUrl ?? null,
                        }
                      : null,
                  }));
                  if (c) setErrors((er) => ({ ...er, toClub: undefined }));
                }}
              />
              {errors.toClub && (
                <p className="text-xs text-red-500 mt-1 font-medium">
                  {errors.toClub}
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="mb-1 block text-sm">
                  Fee (£m) <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  value={form.fee}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, fee: e.target.value }));
                    if (e.target.value !== "")
                      setErrors((er) => ({ ...er, fee: undefined }));
                  }}
                  placeholder="100"
                  min={0}
                  className={errors.fee ? "border-red-400" : ""}
                />
                {errors.fee && (
                  <p className="text-xs text-red-500 mt-1 font-medium">
                    {errors.fee}
                  </p>
                )}
              </div>
              <div>
                <Label className="mb-1 block text-sm">
                  Year <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="number"
                  value={form.year}
                  onChange={(e) => {
                    setForm((f) => ({ ...f, year: e.target.value }));
                    if (e.target.value !== "")
                      setErrors((er) => ({ ...er, year: undefined }));
                  }}
                  placeholder="2023"
                  min={1900}
                  max={2100}
                  className={errors.year ? "border-red-400" : ""}
                />
                {errors.year && (
                  <p className="text-xs text-red-500 mt-1 font-medium">
                    {errors.year}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label className="mb-1 block text-sm">Display Order</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) =>
                  setForm((f) => ({ ...f, order: e.target.value }))
                }
                placeholder="0"
                min={0}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isCreating || isUpdating}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {isCreating || isUpdating
                ? "Saving…"
                : editing
                  ? "Update"
                  : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Pagination control (shared) ─────────────────────────────────────────────

function Pagination({
  page,
  totalPages,
  total,
  setPage,
}: {
  page: number;
  totalPages: number;
  total: number;
  setPage: (n: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mt-4 text-sm text-gray-500">
      <span>
        {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of{" "}
        {total}
      </span>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={page === 1}
          onClick={() => setPage(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {Array.from({ length: totalPages }, (_, i) => i + 1)
          .filter((n) => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
          .reduce<(number | "…")[]>((acc, n, idx, arr) => {
            if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push("…");
            acc.push(n);
            return acc;
          }, [])
          .map((n, i) =>
            n === "…" ? (
              <span key={`e-${i}`} className="px-1">
                …
              </span>
            ) : (
              <Button
                key={n}
                variant={n === page ? "default" : "outline"}
                size="sm"
                className={cn(
                  "h-8 w-8 p-0",
                  n === page &&
                    "bg-orange-500 hover:bg-orange-600 text-white",
                )}
                onClick={() => setPage(n as number)}
              >
                {n}
              </Button>
            ),
          )}
        <Button
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={page === totalPages}
          onClick={() => setPage(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// ─── Page shell ──────────────────────────────────────────────────────────────

type Tab = "GOALS" | "ASSISTS" | "TRANSFERS";

export default function HigherOrLowerPage() {
  const [tab, setTab] = useState<Tab>("GOALS");

  return (
    <Layout>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart2 className="h-8 w-8 text-orange-500" />
            Higher or Lower
          </h1>
          <p className="text-gray-500 mt-1 text-sm">
            Manage the three game modes: career goals, career assists, and
            transfer fees.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="inline-flex rounded-lg border bg-white shadow-sm mb-6 p-1">
        {(
          [
            { key: "GOALS", label: "Goals" },
            { key: "ASSISTS", label: "Assists" },
            { key: "TRANSFERS", label: "Transfers" },
          ] as const
        ).map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md transition-colors",
              tab === t.key
                ? "bg-orange-500 text-white shadow-sm"
                : "text-gray-600 hover:text-gray-900 hover:bg-gray-50",
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "GOALS" && <StatTab mode="GOALS" />}
      {tab === "ASSISTS" && <StatTab mode="ASSISTS" />}
      {tab === "TRANSFERS" && <TransfersTab />}
    </Layout>
  );
}
